import { getAppType, AppType, CORDOVA_UNDEFINED_MSG, NATIVESDK_UNDEFINED_MSG, NOT_NATIVE_APP_MSG } from "./nativeHelpers";
import { IFileEntry, IFileWriter } from "../../model/File";
import idSerializer from "geotab-uuid";
import * as logger from "../../utils/logger";
import { handleApi } from "../error";

export enum PictureSourceType {
    PHOTOLIBRARY = 0,
    CAMERA = 1
}
export interface FileInfo {
    name: string;
    isDir: boolean;
    modifiedDate: string;
    size?: number; // File size, if file is a directory, size is undefined
}
export interface IImageResult {
    mediaId: string;
    filePath: string;
    base64Image: string;
    source?: "camera" | "library" | "web";
    capturedAt?: string;
}

enum DestinationType {
    DATA_URL = 0,
    FILE_URI = 1,
    NATIVE_URI = 2
}

enum EncodingType {
    JPEG = 0,
    PNG = 1
}

function resolveForTesting(resolve, withMediaIdAndFilePath = false): boolean {
    let debugImage = window.driveApp?.debug?.base64TestImage;
    if (debugImage) {
        withMediaIdAndFilePath ? resolve({ mediaId: idSerializer.get(), filePath: idSerializer.get(), base64Image: debugImage }) : resolve(debugImage);
        return true;
    } else {
        return false;
    }
}

function getFileReader(resolve, reject): FileReader {
    let reader = new FileReader();
    reader.onloadend = function() {
        if (!reader.result) {
            reject(new Error("File content is null"));
            return;
        }
        resolve(reader.result);
    };
    reader.onerror = () => {
        logger.event(logger.Event.FILEACTION, `Failed to read file: ${ reader.error?.message }`);
        reject(reader.error);
    };
    return reader;
}

export interface INativeFileController {
    /**
     * @param filename
     */
    getImageFilePathOnDevice(filename: string): string;
    /**
     * @param fileName
     * @param fileContents: can be Blob or string
     */
    createAndWriteNewFile(fileName: string, fileContents: Blob | string): Promise<any>;
    /**
     * @param filepath: is a @string which is the fullpath of a file in nativeSDK application. For cordova app, we have to send a @IFileEntry
     * @param isBinary: if the value is true, the file will be read as a binary file. otherwise it will be read as text file
     */
    readFile(filepath: string | IFileEntry, isBinary: boolean): Promise<string | ArrayBuffer>;
    /**
     * @param source: source can be either camera or photolibrary
     */
    getPicture(source: PictureSourceType): Promise<IImageResult>;
    /**
     * @param filePath: fullpath
     */
    getBase64Data(filePath: string): Promise<string>;
    /**
     * @param filePath: fullpath
     */
    removeFile(filePath: string): Promise<string>;
    /********** ONLY NATIVESDK APP SUPPORTS THE FOLLOWING APIs ************/
    /********
     * List files in a directory
     * @param directoryPath: string
     *  drvfs path of the directory
     */
    list(directoryPath: string): Promise<FileInfo[]>;
    /********
     * Get File Info
     * @param filePath: string
     *  drvfs path of the file
     */
    getFileInfo(filePath: string): Promise<FileInfo>;
    /********
     * Move File
     * @param argument:
     *  srcPath: drvfs path of the source file. Directories cannot be specified.
     *  dstPath: drvfs path of the destination file. Directories cannot be specified.
     *  overwrite: Optional. Should the destination file be overwritten if it already exists. Defaults to false (do not overwrite).
     */
    moveFile(srcPath: string, dstPath: string, overwrite?: boolean): Promise<string>;
    /********
     * Delete an empty folder. it will fail if the folder is not empty.
     * @param folderPath: string
     *  drvfs path of the folder
     */
    deleteFolder(folderPath: string): Promise<string>;
}

export let isPNGSupported = function () {
    if (getAppType() === AppType.CordovaApp && window.device && window.device.platform === "Android") {
        let version = window.device.version;
        if (version && parseInt(version) >= 11) {
            return false;
        }
    }
    return true;
};

class CordovaFileController implements INativeFileController {
    getImageFilePathOnDevice(filename: string): string {
        if (window.cordova) {
            return `${ window.cordova.file.cacheDirectory }${ filename }`;
        } else {
            return "";
        }
    }

    createAndWriteNewFile(fileName: string, fileContents: string | Blob): Promise<any> {
        return new Promise((resolve, reject) => {
            this.createNewFile(fileName)
                .then((fileEntry: IFileEntry) => this.writeFile(fileEntry, fileContents))
                .then((nativeURL: string) => resolve(nativeURL))
                .catch(reject);
        });
    }

    getPicture(source: PictureSourceType): Promise<IImageResult> {
        return new Promise((resolve, reject) => {
            if (resolveForTesting(resolve, true)) {
                return;
            }
            let success = (picturePath: string) => {
                this.getFileEntry(picturePath).then((fileEntry: IFileEntry) => {
                    this.readFileAsDataURL(fileEntry).then((base64DataWithPrefix) => {
                        let pictureName = idSerializer.get();

                        logger.event(logger.Event.IMAGEACTION, `Received new picture data ${ pictureName }`);

                        this.createAndWriteNewFile(pictureName, base64DataWithPrefix).then((nativeURL: string) => {
                            resolve({ mediaId: pictureName, filePath: nativeURL, base64Image: base64DataWithPrefix});
                            this.removeFile(picturePath).catch(handleApi);
                        }).catch(reject);
                    }).catch(reject);
                }).catch(err => {
                    logger.event(logger.Event.FILEACTION, `Failed to save image: ${ err }`);
                    reject(err);
                });
            };
            let failure = (error: string) => {
                logger.event(logger.Event.IMAGEACTION, `Failed to capture/upload: ${ error }`);
                reject(error);
            };
            let navigator = window.navigator as any;
            let canUsePNG = isPNGSupported();
            let imageOptions = {
                sourceType: source,
                destinationType: DestinationType.FILE_URI,
                encodingType: canUsePNG ? EncodingType.PNG : EncodingType.JPEG,
                targetWidth: 500,
                targetHeight: 500,
                correctOrientation: true,
                allowEdit: canUsePNG,
                quality: 20
            };

            if (navigator.camera) {
                navigator.camera.getPicture(success, failure, imageOptions);
            } else {
                reject(new Error("Native camera property on navigator does not exist."));
            }
        });
    }

    getBase64Data(filePath: string): Promise<string> {
        return new Promise((resolve, reject) => {
            if (resolveForTesting(resolve)) {
                return;
            }
            this.getFileEntry(filePath).then((fileEntry) => {
                this.readFileAsText(fileEntry).then((base64String) => {
                    resolve(base64String);
                }).catch(reject);
            }).catch((error: Error) => {
                logger.event(logger.Event.FILEACTION, `Image does not exist locally. Error: ${ error.toString() }`);
                resolve(""); // Image does not exist locally, must be downloaded
            });
        });
    }

    removeFile(filePath: string): Promise<string> {
        return new Promise((resolve, reject) => {
            this.getFileEntry(filePath).then((fileEntry: IFileEntry) => {
                fileEntry.remove(() => {
                    resolve("File successfully removed");
                }, (e: string) => {
                    reject(e);
                });
            }).catch((error: Error) => {
                logger.event(logger.Event.FILEACTION, `Failed to remove file. Error: ${ error.toString() }`);
                reject(error);
            });
        });
    }

    createNewFile(fileName: string): Promise<IFileEntry> {
        return new Promise((resolve, reject) => {
            if (!window.cordova) {
                reject(new Error(CORDOVA_UNDEFINED_MSG));
                return;
            }
            let success = (fileEntry: IFileEntry) => {
                resolve(fileEntry);
            };
            let failure = error => {
                reject(error);
            };
            (window as any).resolveLocalFileSystemURL(window.cordova.file.cacheDirectory, (dirEntry: IFileEntry) => {
                dirEntry.getFile(fileName, { create: true, exclusive: false }, success, failure);
            }, reject);
        });
    }

    writeFileAsBinary(fileEntry: IFileEntry, fileContents: Blob) {
        return new Promise<string>((resolve, reject) => {
            fileEntry.createWriter((fileWriter: IFileWriter) => {
                fileWriter.onwriteend = () => {
                    resolve(fileEntry.nativeURL);
                };
                fileWriter.onerror = (e) => {
                    reject(e);
                };

                let reader = getFileReader(resolve, reject);
                reader.onloadend = function() {
                    if (!reader.result) {
                        reject(new Error("File is empty"));
                        return;
                    }
                    fileWriter.write(reader.result);
                };
                reader.readAsDataURL(fileContents);
            }, reject);
        });
    }

    writeFileAsText(fileEntry: IFileEntry, fileContents: string) {
        return new Promise<string>((resolve, reject) => {
            fileEntry.createWriter((fileWriter: IFileWriter) => {
                fileWriter.onwriteend = () => {
                    resolve(fileEntry.nativeURL);
                };
                fileWriter.onerror = (e) => {
                    reject(e);
                };

                fileWriter.write(fileContents);
            }, reject);
        });
    }

    writeFile(fileEntry: IFileEntry, fileContents: Blob | string) {
        if (typeof fileContents === "string") {
            return this.writeFileAsText(fileEntry, fileContents);
        } else {
            return this.writeFileAsBinary(fileEntry, fileContents);
        }
    }

    getFileEntry(filePath: string): Promise<IFileEntry> {
        return new Promise((resolve, reject) => {
            (window as any).resolveLocalFileSystemURL(filePath, resolve, reject);
        });
    }

    readFile(filepath: string | IFileEntry, isBinary = false): Promise<string | ArrayBuffer> {
        if (typeof filepath === "string") {
            return Promise.reject(new Error("Expected filepath type: IFileEntry"));
        }
        if (!isBinary) {
            return this.readFileAsText(filepath);
        } else {
            return this.readFileAsDataURL(filepath);
        }
    }

    readFileAsText(filepath: IFileEntry): Promise<string> {
        return new Promise((resolve, reject) => {
            filepath.file(file => {
                let reader = getFileReader(resolve, reject);
                reader.readAsText(file);
            }, error => {
                logger.event(logger.Event.FILEACTION, `Failed to read text file: ${ error }`);
                reject(error);
            });
        });
    }

    readFileAsDataURL(filepath: IFileEntry): Promise<string> {
        return new Promise((resolve, reject) => {
            filepath.file(file => {
                let reader = getFileReader(resolve, reject);
                reader.readAsDataURL(file);
            }, error => {
                logger.event(logger.Event.FILEACTION, `Failed to read file as DataURL: ${ error }`);
                reject(error);
            });
        });
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    list(_directoryPath: string): Promise<FileInfo[]> {
        return Promise.reject(new Error(NATIVESDK_UNDEFINED_MSG));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getFileInfo(_filePath: string): Promise<FileInfo> {
        return Promise.reject(new Error(NATIVESDK_UNDEFINED_MSG));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    moveFile(_srcPath: string, _dstPath: string, _overwrite?: boolean | undefined): Promise<string> {
        return Promise.reject(new Error(NATIVESDK_UNDEFINED_MSG));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    deleteFolder(_folderPath: string): Promise<string> {
        return Promise.reject(new Error(NATIVESDK_UNDEFINED_MSG));
    }
}

export function arrayBufferToBase64(buffer: ArrayBuffer): Promise<string> {
    return new Promise((resolve, reject) => {
        let blob = new Blob([buffer]);
        let reader = getFileReader(resolve, reject);
        reader.readAsDataURL(blob);
    });
}

class NativeSDKFileController implements INativeFileController {
    getImageFilePathOnDevice(filename: string): string {
        return `drvfs:///${ filename }.png`;
    }

    createAndWriteNewFile(fileName: string, fileContents: string | Blob): Promise<any> {
        return new Promise((resolve, reject) => {
            if (!window.geotabModules) {
                reject(new Error(NATIVESDK_UNDEFINED_MSG));
                return;
            }
            let newPath = this.getImageFilePathOnDevice(fileName);
            if (typeof fileContents === "string") {
                this.writeFileAsText(newPath, fileContents).then(_ => {
                    resolve(newPath);
                }).catch(reject);
            } else {
                let fileReader = getFileReader(resolve, reject);
                fileReader.onloadend = () => {
                    if (!fileReader.result) {
                        reject(new Error("File content is null"));
                        return;
                    }
                    this.writeFileAsBinary(newPath, fileReader.result as ArrayBuffer).then(_ => {
                        resolve(newPath);
                    }).catch(reject);
                };
                //read Blob data and return as ArrayBuffer
                fileReader.readAsArrayBuffer(fileContents);
            }
        });
    }

    getPicture(source: PictureSourceType): Promise<IImageResult> {
        return new Promise((resolve, reject) => {
            if (resolveForTesting(resolve, true)) {
                return;
            }
            if (!window.geotabModules) {
                reject(new Error(NATIVESDK_UNDEFINED_MSG));
                return;
            }
            let pictureName = idSerializer.get();
            const widthHeight = 1000;
            let completionHandler = (filePath) => {
                this.getBase64Data(filePath).then((base64Data: string) => {
                    resolve({ mediaId: pictureName, filePath: filePath, base64Image: base64Data });
                }).catch(reject);
                logger.event(logger.Event.IMAGEACTION, `Received new picture data ${ pictureName }`);
            };
            if (source === PictureSourceType.CAMERA) {
                window.geotabModules.camera.captureImage({ fileName: pictureName, size: { width: widthHeight, height: widthHeight } }, (err: Error | undefined, filePath: string | undefined) => {
                    if (err) {
                        logger.event(logger.Event.IMAGEACTION, `Failed to capture image: ${ err.message }`);
                        reject(err);
                    } else {
                        completionHandler(filePath);
                    }
                });
            } else if (source === PictureSourceType.PHOTOLIBRARY) {
                window.geotabModules.photoLibrary.pickImage({ fileName: pictureName, size: { width: widthHeight, height: widthHeight } }, (err: Error | undefined, filePath: string | undefined) => {
                    if (err) {
                        logger.event(logger.Event.IMAGEACTION, `Failed to upload image: ${ err.message }`);
                        reject(err);
                    } else {
                        completionHandler(filePath);
                    }
                });
            }
        });
    }

    getBase64Data(filePath: string): Promise<string> {
        return new Promise((resolve, reject) => {
            if (resolveForTesting(resolve)) {
                return;
            }
            this.readFileAsBinary(filePath).then((buffer: ArrayBuffer) => {
                arrayBufferToBase64(buffer).then((base64: string) => {
                    resolve(base64);
                }).catch(reject);
            }).catch((error: Error) => {
                logger.event(logger.Event.FILEACTION, `Image does not exist locally. Error: ${ error.toString() }`);
                resolve(""); // Image does not exist locally, must be downloaded
            });
        });
    }

    removeFile(filePath: string): Promise<string> {
        return new Promise((resolve, reject) => {
            if (!window.geotabModules) {
                reject(new Error(NATIVESDK_UNDEFINED_MSG));
                return;
            }
            window.geotabModules.fileSystem.deleteFile(filePath, (err: Error | undefined) => {
                if (err) {
                    logger.event(logger.Event.FILEACTION, `Failed to remove file: ${ err.message }`);
                    reject(err);
                } else {
                    resolve("Successfully removed");
                }
            });
        });
    }

    writeFileAsBinary(path: string, data: ArrayBuffer, offset?: number): Promise<number> {
        return new Promise((resolve, reject) => {
            if (!window.geotabModules) {
                reject(new Error(NATIVESDK_UNDEFINED_MSG));
                return;
            }
            window.geotabModules.fileSystem.writeFileAsBinary({path: path, data: data, offset: offset}, (err: Error | undefined, size: number | undefined) => {
                if (err) {
                    logger.event(logger.Event.FILEACTION, `Failed to write file as binary: ${ err.message }`);
                    reject(err);
                } else {
                    resolve(size || 0);
                }
            });
        });
    }

    writeFileAsText(path: string, data: string, offset?: number): Promise<number> {
        return new Promise((resolve, reject) => {
            if (!window.geotabModules) {
                reject(new Error(NATIVESDK_UNDEFINED_MSG));
                return;
            }
            window.geotabModules.fileSystem.writeFileAsText({path: path, data: data, offset: offset}, (err: Error | undefined, size: number | undefined) => {
                if (err) {
                    logger.event(logger.Event.FILEACTION, `Failed to write file as text: ${ err.message }`);
                    reject(err);
                } else {
                    resolve(size || 0);
                }
            });
        });
    }

    readFileAsBinary(filepath: string): Promise<ArrayBuffer> {
        return new Promise((resolve, reject) => {
            if (!window.geotabModules) {
                reject(new Error(NATIVESDK_UNDEFINED_MSG));
                return;
            }
            window.geotabModules.fileSystem.readFileAsBinary({path: filepath }, (err: Error | undefined, result: ArrayBuffer | undefined) => {
                if (result) {
                    resolve(result);
                } else {
                    logger.event(logger.Event.FILEACTION, `Failed to read file as binary: ${ err?.message }`);
                    reject(err);
                }
            });
        });
    }

    readFileAsText(filepath: string): Promise<string> {
        return new Promise((resolve, reject) => {
            if (!window.geotabModules) {
                reject(new Error(NATIVESDK_UNDEFINED_MSG));
                return;
            }
            window.geotabModules.fileSystem.readFileAsText({path: filepath }, (err: Error | undefined, result: string | undefined) => {
                if (result) {
                    resolve(result);
                } else {
                    logger.event(logger.Event.FILEACTION, `Failed to read file as text: ${ err?.message }`);
                    reject(err);
                }
            });
        });
    }

    readFile(filepath: string | IFileEntry, isBinary = false): Promise<string | ArrayBuffer> {
        return new Promise((resolve, reject) => {
            if (typeof filepath !== "string") {
                reject(new Error("Filepath is invalid"));
                return;
            }

            if (isBinary) {
                this.readFileAsBinary(filepath).then(resolve).catch(reject);
            } else {
                this.readFileAsText(filepath).then(resolve).catch(reject);
            }
        });
    }
    //For example: "drvfs:///" will give all the files in drvfs directory
    list(directoryPath: string): Promise<FileInfo[]> {
        return new Promise((resolve, reject) => {
            if (!window.geotabModules) {
                reject(new Error(NATIVESDK_UNDEFINED_MSG));
                return;
            }
            window.geotabModules.fileSystem.list(directoryPath, (error?: Error, result?: FileInfo[]) => {
                if (error) {
                    logger.event(logger.Event.FILEACTION, `Failed to get files ${ error.message }`);
                    reject(error);
                } else {
                    resolve(result || []);
                }
            });
        });
    }

    getFileInfo(filePath: string): Promise<FileInfo> {
        return new Promise((resolve, reject) => {
            if (!window.geotabModules) {
                reject(new Error(NATIVESDK_UNDEFINED_MSG));
                return;
            }
            window.geotabModules.fileSystem.getFileInfo(filePath, (err?: Error, result?: FileInfo) => {
                if (err) {
                    logger.event(logger.Event.FILEACTION, `Failed to get file info: ${ err.message }`);
                    reject(err);
                } else {
                    result && resolve(result);
                }
            });
        });
    }

    moveFile(srcPath: string, dstPath: string, overwrite?: boolean): Promise<string> {
        return new Promise((resolve, reject) => {
            if (!window.geotabModules) {
                reject(new Error(NATIVESDK_UNDEFINED_MSG));
                return;
            }
            window.geotabModules.fileSystem.moveFile({ srcPath, dstPath, overwrite}, (err?: Error, _?: undefined) => {
                if (err) {
                    logger.event(logger.Event.FILEACTION, `Failed to move file: ${ err.message }`);
                    reject(err);
                } else {
                    resolve(dstPath);
                }
            });
        });
    }

    deleteFolder(folderPath: string): Promise<string> {
        return new Promise((resolve, reject) => {
            if (!window.geotabModules) {
                reject(new Error(NATIVESDK_UNDEFINED_MSG));
                return;
            }
            window.geotabModules?.fileSystem.deleteFolder(folderPath, (err?: Error, _?: undefined) => {
                if (err) {
                    logger.event(logger.Event.FILEACTION, `Failed to delete folder: ${ err.message }`);
                    reject(err);
                } else {
                    resolve(folderPath);
                }
            });
        });
    }
}

class WebAppFileController implements INativeFileController {

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getImageFilePathOnDevice(_filename: string): string {
        return "";
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    createAndWriteNewFile(_fileName: string, _fileContents: string | Blob): Promise<any> {
        return Promise.reject(new Error(NOT_NATIVE_APP_MSG));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getPicture(_source: PictureSourceType): Promise<IImageResult> {
        return new Promise((resolve, reject) => {
            if (resolveForTesting(resolve, true)) {
                return;
            }
            reject(new Error(NOT_NATIVE_APP_MSG));
        });
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getBase64Data(_filePath: string): Promise<string> {
        return new Promise((resolve, reject) => {
            if (resolveForTesting(resolve)) {
                return;
            }
            reject(new Error(NOT_NATIVE_APP_MSG));
        });
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    removeFile(_filePath: string): Promise<string> {
        return Promise.reject(new Error(NOT_NATIVE_APP_MSG));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    readFile(_filepath: string | IFileEntry, _isBinary: boolean): Promise<string | ArrayBuffer> {
        return Promise.reject(new Error(NOT_NATIVE_APP_MSG));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    list(_directoryPath: string): Promise<FileInfo[]> {
        return Promise.reject(new Error(NOT_NATIVE_APP_MSG));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getFileInfo(_filePath: string): Promise<FileInfo> {
        return Promise.reject(new Error(NOT_NATIVE_APP_MSG));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    moveFile(_srcPath: string, _dstPath: string, _overwrite?: boolean): Promise<string> {
        return Promise.reject(new Error(NOT_NATIVE_APP_MSG));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    deleteFolder(_folderPath: string): Promise<string> {
        return Promise.reject(new Error(NOT_NATIVE_APP_MSG));
    }
}

let cordovaFileController: INativeFileController = new CordovaFileController();
let nativeSDKFileController: INativeFileController = new NativeSDKFileController();
let webAppFileController: INativeFileController = new WebAppFileController();
/**
 * @returns fileController based on current AppType
 */
export let getFileController = () => {
    switch (getAppType()) {
        case AppType.CordovaApp:
            return cordovaFileController;
        case AppType.NativeSdk:
            return nativeSDKFileController;
        default:
            return webAppFileController;
    }
};

export function isCameraPluginPresent(): boolean {
    return (window.navigator as any).camera || (!!window.geotabModules && !!window.geotabModules.camera);
}

export function base64StringToArrayBuffer(base64: string) {
    let len = base64.length;
    let bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
        bytes[i] = base64.charCodeAt(i);
    }
    return bytes.buffer;
}